import { DataGridPro } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import React, { useState } from "react";
import { generateExportCsvButtonText } from "../../util/commonUtils.js";
import {
  ACTIONS,
  EMPLOYEE_TEMP_LABEL,
  SHIFT,
  STATUS,
  TIME_OFF_PAGE_TYPE,
  TIME_OFF_TYPE,
  WOP_AUDIT_CURRENT_LIST,
} from "../../util/constants.js";
import {
  convert_Timestamp_To_Luxon_no_local_time,
  format_ShortDOW_Date,
  getFormattedTimeStamp,
  getShiftTime,
  parsePreferredLeaveTime,
  set_time_to_end_of_day,
} from "../../util/dateTimeFormatter.js";
import { getTOREmployeeName, getTORPreferredLeaveTime, getTORShift } from "../../util/timeOffExportMapper.js";
import CustomErrorTableOverlay from "../CustomErrorTableOverlay.jsx";
import CustomLoadingTableOverlay from "../CustomLoadingTableOverlay.jsx";
import CustomNoRowsTableOverlay from "../CustomNoRowsTableOverlay.jsx";
import TimeOffRequestAdminAction from "../TimeOffRequestAdminAction.jsx";
import { isLWOPTOWOPAllDayRequest } from "../../util/timeOffRequestUtils.js";
import TimeOffRequestWOPStatus from "./TimeOffRequestWOPStatus.jsx";
import { DateTime } from "luxon";
import { getTimeZoneFromSwaStation } from "../../util/timeZoneService.js";
import { TOMORROW, YESTERDAY } from "../../util/dateHelper.js";
import { Alert, Box, Chip } from "@mui/material";
import AdminTimeOffRequestWOPToolbar from "./AdminTimeOffRequestWOPToolbar.jsx";
import { selectUserSession } from "../../slices/userSession/userSessionSlice.js";
import { useSelector } from "react-redux";

const useStyles = makeStyles((theme) => ({
  torListOwnItem: {
    background: theme.palette.secondary.swaLightestBlue,
  },
}));

const TimeOffRequestWOPTable = (props) => {
  const classes = useStyles();
  const userSession = useSelector(selectUserSession);
  const [selectionModel] = useState([]);

  const disableActionsForTimeOffRequest = (timeOffRequest) => {
    /*
    Admin actions are disabled for the following conditions:
    1. If any employee is unranked. (Handled by anyUnrankedEmployees prop)
    2. ALL DAY - Starting at midnight of the day before the request start date
    3. DAY OF - At time of request end date
    4. If selected date is after tomorrow
    5. If selected date is before yesterday
    */

    if (props.selectedDate) {
      const now = DateTime.now().setZone(getTimeZoneFromSwaStation(props.station));
      const selectedDate = props.selectedDate.set({
        hour: now.hour,
        minute: now.minute,
        second: now.second,
        millisecond: now.millisecond,
      });

      const yesterdayReadOnlyDeadline = set_time_to_end_of_day(YESTERDAY(props.station));
      const tomorrowReadOnlyDeadline = set_time_to_end_of_day(TOMORROW(props.station));
      if (
        selectedDate.toMillis() > tomorrowReadOnlyDeadline.toMillis() ||
        selectedDate.toMillis() < yesterdayReadOnlyDeadline.toMillis()
      )
        return true; //if selected date is after tomorrow or before yesterday, disable actions

      const requestDeadline = isLWOPTOWOPAllDayRequest(props.wopType)
        ? set_time_to_end_of_day(YESTERDAY(props.station))
        : now;
      const shiftDeadline = isLWOPTOWOPAllDayRequest(props.wopType)
        ? set_time_to_end_of_day(timeOffRequest.shiftStart.minus({ days: 1 })) //midnight of the day before the request start date
        : timeOffRequest.shiftEnd;
      return shiftDeadline.toMillis() < requestDeadline.toMillis();
    }
    return true;
  };

  const renderStatusTypeCell = (timeOffRequest) => {
    return props.isAdminPage ? (
      <TimeOffRequestWOPStatus timeOffRequest={timeOffRequest} isAdminPage={props.isAdminPage} />
    ) : (
      timeOffRequest.visibleStatus
    );
  };

  const renderShiftCell = (timeOffRequest) => {
    return (
      <div className="shift-cell">
        {format_ShortDOW_Date(timeOffRequest.shiftStart) +
          " " +
          getShiftTime(timeOffRequest.shiftStart) +
          "-" +
          getShiftTime(timeOffRequest.shiftEnd)}
      </div>
    );
  };

  const renderApprovedLeaveTimeCell = (timeOffRequest) => {
    return (
      <div className="preferredLeave-cell">
        {timeOffRequest.approvedWOPStart !== "" && timeOffRequest.approvedWOPEnd !== ""
          ? parsePreferredLeaveTime(timeOffRequest.approvedWOPStart, timeOffRequest.approvedWOPEnd)
          : ""}
      </div>
    );
  };

  const renderActionCell = (timeOffRequest) => {
    if (
      !props.anyUnrankedEmployees &&
      props.isWOPListFrozen &&
      !disableActionsForTimeOffRequest(timeOffRequest) &&
      props.isAdminPage
    )
      return <TimeOffRequestAdminAction timeOffRequest={timeOffRequest} />;
  };

  const renderEmployee = (timeOffRequests) => {
    return (
      <>
        {getTOREmployeeName(timeOffRequests)} ({timeOffRequests.empId})
        {timeOffRequests.isTemp && <Chip sx={{ marginLeft: "5px" }} label={EMPLOYEE_TEMP_LABEL} />}
      </>
    );
  };

  const renderFrozenMessage = () => {
    if (props.isWOPListFrozen && props.wopFreezeData) {
      const formatDate = getFormattedTimeStamp(
        convert_Timestamp_To_Luxon_no_local_time(props.wopFreezeData.createdAt, props.station)
      );
      return (
        <Box
          sx={{
            width: "100%",
            height: "50px",
            borderTop: "solid 1px",
            borderLeft: "solid 1px",
            borderRight: "solid 1px",
            borderColor: "secondary.swaGray2",
          }}
        >
          {props.isAdminPage ? (
            <Alert severity="info" style={{ fontWeight: "bold" }}>
              Award is In-Progress. Started by {props.wopFreezeData.modifiedBy.lastName},{" "}
              {props.wopFreezeData.modifiedBy.firstName} at {formatDate}
            </Alert>
          ) : (
            <Alert severity="info" style={{ fontWeight: "bold" }}>
              Award is In-Progress. The list will be available once completed
            </Alert>
          )}
        </Box>
      );
    }
  };

  const renderAuditMessage = () => {
    if (props.isAdminPage && props.currentAuditDateTimeSelection !== WOP_AUDIT_CURRENT_LIST) {
      const freezeStart = getFormattedTimeStamp(
        convert_Timestamp_To_Luxon_no_local_time(props.wopFreezeData.freezeTime, props.station)
      );
      const freezeEnd = getFormattedTimeStamp(
        convert_Timestamp_To_Luxon_no_local_time(props.wopFreezeData.createdAt, props.station)
      );
      const modifiedBy = props.wopFreezeData.modifiedBy?.firstName
        ? `${props.wopFreezeData.modifiedBy.lastName}, ${props.wopFreezeData.modifiedBy.firstName} (${props.wopFreezeData.modifiedBy.eid})`
        : "SYSTEM";
      const message = `Award Start: ${props.isLoading ? "Loading..." : freezeStart}\n Award End: ${
        props.isLoading ? "Loading..." : freezeEnd
      }\nCompleted by ${props.isLoading ? "Loading..." : modifiedBy}`;
      return (
        <Box
          sx={{
            width: "100%",
            height: "85px",
            borderTop: "solid 1px",
            borderLeft: "solid 1px",
            borderRight: "solid 1px",
            borderColor: "secondary.swaGray2",
          }}
        >
          <Alert
            severity="info"
            sx={{ whiteSpace: "pre-line", textAlign: "left", "& .MuiAlert-icon": { marginTop: "20px" } }}
            style={{ fontWeight: "bold" }}
          >
            {message}
          </Alert>
        </Box>
      );
    }
    return "";
  };

  const getRowClassName = (params) => {
    if (!props.isAdminPage && props.isDesktopView) {
      if (params.row.empId === userSession.eid) {
        return classes.torListOwnItem;
      }
    }
  };

  const renderTimeOffTable = () => {
    const columns = [
      {
        field: "submittedDate",
        headerName: "Submitted",
        type: "dateTime",
        flex: 0.2,
        valueGetter: (params) => {
          return params.row.submittedDate ? params.row.submittedDate : params.row.approvedDate;
        },
        valueFormatter: ({ value }) => {
          return format_ShortDOW_Date(value) + " " + getShiftTime(value);
        },
        sortable: false,
      },
      {
        field: "shift",
        headerName: SHIFT,
        type: "string",
        flex: 0.4,
        valueGetter: (params) => {
          return params.row;
        },
        renderCell: (params) => {
          return renderShiftCell(params.row);
        },
        valueFormatter: ({ value }) => {
          return getTORShift(value);
        },
        sortable: false,
      },
      {
        field: "visibleStatus",
        headerName: STATUS,
        type: "string",
        flex: 0.15,
        renderCell: (params) => {
          return renderStatusTypeCell(params.row);
        },
        sortable: false,
      },
      { field: "rank", headerName: "Seniority Rank", type: "string", flex: 0.16, sortable: false },
      {
        field: "employee",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => {
          return getTOREmployeeName(value);
        },
        renderCell: (params) => {
          return renderEmployee(params.row);
        },
        headerName: "Employee",
        type: "string",
        flex: 0.4,
        sortable: false,
      },
      {
        field: "phoneNumber",
        headerName: "Phone #",
        type: "string",
        flex: 0.4,
        sortable: false,
        hide: !props.isAdminPage,
      },
      {
        field: "preferredWOPLeaveTime",
        headerName: "Preferred Leave Time",
        flex: 0.4,
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => {
          return getTORPreferredLeaveTime(value);
        },
        type: "string",
        sortable: false,
        hide: isLWOPTOWOPAllDayRequest(props.wopType) || props.wopType === "",
      },
      {
        field: "approvedWOPLeaveTime",
        headerName: "Approved Leave Time",
        flex: 0.4,
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => {
          return value.approvedWOPStart && value.approvedWOPEnd
            ? parsePreferredLeaveTime(value.approvedWOPStart, value.approvedWOPEnd)
            : "";
        },
        renderCell: (params) => {
          return renderApprovedLeaveTimeCell(params.row);
        },
        type: "string",
        sortable: false,
        hide: isLWOPTOWOPAllDayRequest(props.wopType) || props.wopType === "",
      },
      {
        field: "beforeWOPDeadline",
        headerName: "Above Deadline",
        flex: 0.4,
        renderCell: (params) => {
          return params.row.beforeWOPDeadline ? "Yes" : "No";
        },
        valueFormatter: ({ value }) => {
          return value ? "Yes" : "No";
        },
        type: "string",
        sortable: false,
        hide: !isLWOPTOWOPAllDayRequest(props.wopType) || props.wopType === "",
      },
      {
        field: "actions",
        headerName: ACTIONS,
        type: "string",
        flex: 0.17,
        sortable: false,
        hide: !props.isAdminPage,
        renderCell: (params) => {
          return renderActionCell(params.row);
        },
      },
      {
        field: "station",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => {
          return value.station;
        },
        type: "string",
        headerName: "Station",
        hide: true,
      },
      {
        field: "referenceNumber",
        headerName: "Reference #",
        type: "string",
        hide: true,
      },
      {
        field: "timeOffRequestType",
        headerName: TIME_OFF_TYPE,
        type: "string",
        hide: true,
      },
      { field: "workgroup", headerName: "Workgroup", type: "string", hide: true },
      {
        field: "employeeId",
        headerName: "EE ID",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => {
          return value.empId;
        },
        type: "string",
        hide: true,
      },
    ];

    return (
      <div style={{ width: "100%", height: "inherit" }}>
        {renderFrozenMessage()}
        {renderAuditMessage()}
        {props.anyUnrankedEmployees && props.isAdminPage && (
          <Box
            sx={{
              width: "100%",
              height: "50px",
              borderTop: "solid 1px",
              borderLeft: "solid 1px",
              borderRight: "solid 1px",
              borderColor: "secondary.swaGray2",
            }}
          >
            <Alert severity="error" style={{ fontWeight: "bold" }}>
              1 or more employees are unranked. Update the seniority list to proceed with the award.
            </Alert>
          </Box>
        )}
        <DataGridPro
          apiRef={props.torTableDesktopRef}
          getRowClassName={getRowClassName}
          rows={props.timeOffRequests}
          columns={columns}
          disableColumnMenu={true}
          selectionModel={selectionModel}
          onCellClick={(params, event) => {
            event.defaultMuiPrevented = true;
          }}
          hideFooterPagination={true}
          rowHeight={props.isAdminPage ? 90 : 60}
          components={{
            LoadingOverlay: CustomLoadingTableOverlay,
            NoRowsOverlay: CustomNoRowsTableOverlay,
            ErrorOverlay: CustomErrorTableOverlay,
            Toolbar: props.isAdminPage ? AdminTimeOffRequestWOPToolbar : null,
          }}
          componentsProps={{
            noRowsOverlay: {
              pageType: TIME_OFF_PAGE_TYPE,
              isAdminPage: props.isAdminPage,
              isWOPTable: true,
              isWOPListFrozen: props.isWOPListFrozen,
              isDesktopView: props.isDesktopView,
            },
            errorOverlay: {
              errorMessage: props.errorObject.message,
              errorCode: props.errorObject.code,
            },
            toolbar: {
              station: props.station,
              wopType: props.wopType,
              renderAuditSelect: props.renderAuditSelect,
              currentAuditDateTimeSelection: props.currentAuditDateTimeSelection,
            },
          }}
          loading={props.isLoading}
          error={props.isError ? true : undefined}
          localeText={{
            toolbarExport: "Export",
            toolbarExportCSV: generateExportCsvButtonText(selectionModel, props.timeOffRequests),
          }}
        />
      </div>
    );
  };

  return <div className="stars-table">{renderTimeOffTable()}</div>;
};

TimeOffRequestWOPTable.propTypes = {
  timeOffRequests: PropTypes.array.isRequired,
  wopType: PropTypes.string.isRequired,
  anyUnrankedEmployees: PropTypes.bool,
  selectedDate: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  errorObject: PropTypes.object.isRequired,
  station: PropTypes.string,
  torTableDesktopRef: PropTypes.object.isRequired,
  isWOPListFrozen: PropTypes.bool.isRequired,
  wopFreezeData: PropTypes.object,
  isAdminPage: PropTypes.bool.isRequired,
  renderAuditSelect: PropTypes.func,
  currentAuditDateTimeSelection: PropTypes.string,
  isDesktopView: PropTypes.bool.isRequired,
};

export default TimeOffRequestWOPTable;
